import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-sm font-medium transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-dark soft-shadow hover:soft-shadow-lg hover-lift",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 soft-shadow hover:soft-shadow-lg",
        outline: "border-2 border-border bg-background hover:bg-secondary hover:border-primary/40 soft-shadow-sm",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/60 soft-shadow-sm",
        ghost: "hover:bg-secondary hover:text-foreground transition-smooth-fast",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary-dark",
        glass: "glass text-foreground hover:glass-strong soft-shadow hover:soft-shadow-lg hover-lift",
        premium: "bg-primary text-primary-foreground font-semibold rounded-full hover:bg-primary-dark transition-all duration-300",
      },
      size: {
        default: "h-11 px-5 py-2.5",
        sm: "h-9 rounded-xl px-4 text-xs",
        lg: "h-13 rounded-2xl px-8 text-base",
        xl: "h-16 rounded-3xl px-10 text-lg",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
